/**
 * File: userTypes.ts
 * Path: packages/types/src/userTypes.ts
 * Description: Staff roles, what each role may do, and the custom claims that
 * carry a role. The one source of truth for the admin app and the server;
 * firestore.rules and storage.rules mirror ROLE_PERMISSIONS by hand and
 * tests/rules asserts they agree.
 */

/**
 * Every role an account can hold. `viewer` is "no access": a signed-in
 * account with no staff role, refused by the admin app.
 */
export type UserRole =
  | "superAdmin"
  | "admin"
  | "developer"
  | "editor"
  | "sales"
  | "viewer";

/** Listed highest first; the user directory and role pickers use this order. */
export const USER_ROLES: readonly UserRole[] = [
  "superAdmin",
  "admin",
  "developer",
  "editor",
  "sales",
  "viewer",
];

export const ROLE_LABELS: Record<UserRole, string> = {
  superAdmin: "Super admin",
  admin: "Admin",
  developer: "Developer",
  editor: "Editor",
  sales: "Sales",
  viewer: "No access",
};

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  superAdmin: "Everything, including adding and removing admins.",
  admin: "Runs the business. Can add developers, editors and sales staff.",
  developer: "Website content, tools and traffic for building and testing. No business records.",
  editor: "Website content: case studies, articles, service wording and reviews.",
  sales: "Leads, clients and the audit, quote and brand tools.",
  viewer: "Can sign in but can't open the admin.",
};

/**
 * What a role may do. Each one gates admin pages and the matching server
 * callables and Firestore collections.
 *
 * - `analytics`: website traffic from Google Analytics
 * - `sales`: leads (incl. Google Maps search) and clients
 * - `referrals`: referral partners and their commission payouts
 * - `enquiries`: website contact-form submissions
 * - `content`: portfolio, academy, services wording, reviews, Search & AI
 * - `tools`: the audit, quote and brand tools in staff mode
 * - `users`: the staff directory (which roles you can hand out is
 *   decided by `canAssignRole`)
 * - `logs`: the account change log
 * - `finance`: the company's books (Accounting). A person's own books are
 *   readable only by that person, on top of this permission.
 */
export type Permission =
  | "analytics"
  | "sales"
  | "referrals"
  | "enquiries"
  | "content"
  | "tools"
  | "users"
  | "logs"
  | "finance";

export const ROLE_PERMISSIONS: Record<UserRole, readonly Permission[]> = {
  // Liam, 2026-10-08: the books are for him and admins only.
  superAdmin: ["analytics", "sales", "referrals", "enquiries", "content", "tools", "users", "logs", "finance"],
  admin: ["analytics", "sales", "referrals", "enquiries", "content", "tools", "users", "logs", "finance"],
  // Liam, 2026-10-01: developers help build and test, so no business
  // records (leads, clients, referrals, enquiries, logs).
  developer: ["analytics", "content", "tools"],
  editor: ["content"],
  sales: ["sales", "tools"],
  viewer: [],
};

/**
 * The custom claims stored on an account. `role` is the truth; `admin` and
 * `editor` are kept because rules and older server code read them, and they
 * are always derived from `role` by `claimsForRole`.
 */
export interface RoleClaims {
  /** Full business access: super admin and admin. */
  admin?: boolean;
  /** May edit website content. */
  editor?: boolean;
  role: UserRole;
}

export const isUserRole = (value: unknown): value is UserRole =>
  typeof value === "string" && (USER_ROLES as readonly string[]).includes(value);

/** Claims for an account holding `role`. */
export const claimsForRole = (role: UserRole): RoleClaims => ({
  admin: role === "superAdmin" || role === "admin",
  editor: ROLE_PERMISSIONS[role].includes("content"),
  role,
});

/**
 * The role a set of claims stands for. Accepts a decoded ID token too.
 *
 * Tokens minted before `role` was always set carry only the booleans, so
 * those map to the role they meant then. Anything unrecognised is `viewer`:
 * an unknown role must never open a door.
 */
export const roleFromClaims = (
  token: object | null | undefined,
): UserRole => {
  if (!token) return "viewer";
  const claims = token as { role?: unknown; admin?: unknown; editor?: unknown };
  if (isUserRole(claims.role)) return claims.role;
  if (claims.role === undefined || claims.role === null) {
    if (claims.admin === true) return "admin";
    if (claims.editor === true) return "editor";
  }
  return "viewer";
};

export const roleCan = (role: UserRole, permission: Permission): boolean =>
  ROLE_PERMISSIONS[role].includes(permission);

/** True for any role that may open the admin app. */
export const isStaffRole = (role: UserRole): boolean => role !== "viewer";

/** Super admins and admins. */
export const isAdminRole = (role: UserRole): boolean =>
  role === "superAdmin" || role === "admin";

/**
 * Whether `actor` may move an account from `currentRole` to `nextRole`.
 *
 * A super admin may set any role. An admin manages only the roles below
 * admin: they can't create admins or touch an admin's account. Nobody else
 * manages roles. The rules no actor can bypass (your own account, the last
 * super admin) live with the caller, which knows who the target is.
 */
export const canAssignRole = (
  actor: UserRole,
  currentRole: UserRole,
  nextRole: UserRole,
): boolean => {
  if (actor === "superAdmin") return true;
  if (actor !== "admin") return false;
  return !isAdminRole(currentRole) && !isAdminRole(nextRole);
};

/** Whether `actor` may switch an account holding `targetRole` off or on. */
export const canManageAccount = (actor: UserRole, targetRole: UserRole): boolean =>
  canAssignRole(actor, targetRole, targetRole);

/** The roles `actor` may hand out, highest first. */
export const assignableRoles = (actor: UserRole): UserRole[] =>
  USER_ROLES.filter((role) => canAssignRole(actor, "viewer", role));

export interface UserProfile {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  role: UserRole;
  createdAt: number;
  updatedAt: number;
  disabled: boolean;
}

export const isAdmin = (claims: RoleClaims | null | undefined): boolean =>
  isAdminRole(roleFromClaims(claims));

export const isEditor = (claims: RoleClaims | null | undefined): boolean =>
  roleCan(roleFromClaims(claims), "content");

export const isViewer = (claims: RoleClaims | null | undefined): boolean =>
  !isStaffRole(roleFromClaims(claims));
